import { trackCotacaoStepView, trackInfoAdicional } from "@/analytics/tracking";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CustomPopover } from "@/components/ui/popover-custom";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useQuoteContext } from "@/contexts/quote-context";
import { useMediaQuery } from "@/hooks/use-media-query";
import { consultarPlaca } from "@/services/flowQuote";
import { formatMoneyBr } from "@/utils/format";
import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";
import { addDays, format, isBefore, parse } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  ArrowLeft,
  ArrowRight,
  CalendarIcon,
  ChevronDown,
  ChevronUp,
  Loader2,
  Search,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { GoAlert } from "react-icons/go";
import * as z from "zod";
import CardContactSpecialist from "../card-contact-specialist";
import { CepOptionCard } from "../cep-option-card";
import Disclaimer from "../disclaimer";
import QuoteCardResume from "../quote-card-resume";
import QuoteCardResumeMobile from "../quote-card-resume-mobile";

const vehicleFormSchema = z.object({
  isNewVehicle: z.enum(["Sim", "Não"]),
  plate: z.string().optional().or(z.literal("")),
  chassis: z.string().optional().or(z.literal("")),
  model: z.string().optional().or(z.literal("")),
  year: z.string().optional().or(z.literal("")),
  color: z.string().optional().or(z.literal("")),
  cepColeta: z
    .string()
    .regex(/^[0-9]{5}-?[0-9]{3}$/, "CEP deve ter o formato 00000-000")
    .optional()
    .or(z.literal("")),
  cepEntrega: z
    .string()
    .regex(/^[0-9]{5}-?[0-9]{3}$/, "CEP deve ter o formato 00000-000")
    .optional()
    .or(z.literal("")),
  complementoColeta: z.string().optional().or(z.literal("")),
  complementoEntrega: z.string().optional().or(z.literal("")),
});

type VehicleFormValues = z.infer<typeof vehicleFormSchema>;

interface VehicleInfoProps {
  onNext: () => void;
  onBack: () => void;
}

export default function VehicleInfo({ onNext, onBack }: VehicleInfoProps) {
  const {
    cotacao,
    quotePrice,
    quoteModifiers,
    updateModifierSeguro,
    updateModifierColeta,
    updateModifierEntrega,
    updateCEPColeta,
    updateCEPEntrega,
    updateDateEntrega,
  } = useQuoteContext();
  const isMobile = useMediaQuery("(max-width: 767px)");
  const [isOrderSummaryExpanded, setIsOrderSummaryExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearchingPlate, setIsSearchingPlate] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [fieldsDisabled, setFieldsDisabled] = useState(true);

  const [selectedOptions, setSelectedOptions] = useState({
    seguro: quoteModifiers.seguro || false,
    coleta: quoteModifiers.coleta || false,
    entrega: quoteModifiers.entrega || false,
  });

  const toggleOption = (option: "seguro") => {
    setSelectedOptions((prev) => ({
      ...prev,
      [option]: !prev[option],
    }));
    if (option === "seguro") {
      updateModifierSeguro(!selectedOptions.seguro);
    }
  };

  const date = cotacao?.cve_data_previsao_recebimento
    ? parse(cotacao.cve_data_previsao_recebimento, "dd/MM/yyyy", new Date())
    : new Date();

  const minDate = addDays(new Date(), 1);
  const isDateDisabled = (date: Date) => isBefore(date, minDate);

  const logDateChange = (newDate: Date | undefined) => {
    if (newDate) {
      updateDateEntrega(newDate);
    }
  };

  const defaultValues: Partial<VehicleFormValues> = {
    isNewVehicle: "Não",
    plate: cotacao.cve_placa || "",
    chassis: cotacao.cve_chassi || "",
    model: cotacao.vmd_descricao || "",
    year: cotacao.cve_ano_mod || "",
    color: cotacao.cor_descricao || "",
    cepColeta: cotacao?.cve_coleta_cep || "",
    cepEntrega: cotacao?.cve_entrega_cep || "",
    complementoColeta: "",
    complementoEntrega: "",
  };

  const form = useForm<VehicleFormValues>({
    resolver: zodResolver(vehicleFormSchema),
    defaultValues,
    mode: "onChange",
  });

  const isNewVehicleValue = form.watch("isNewVehicle");
  const cepColetaValue = form.watch("cepColeta");
  const cepEntregaValue = form.watch("cepEntrega");

  useEffect(() => {
    if (isNewVehicleValue === "Sim") {
      setFieldsDisabled(false);
      setSearchFailed(false);
      setErrorMessage("");
    } else {
      setFieldsDisabled(true);
    }
  }, [isNewVehicleValue]);

  const handleSearchPlate = async () => {
    const plate = form.getValues("plate");
    if (!plate) return;

    setIsSearchingPlate(true);
    setSearchFailed(false);
    setErrorMessage("");

    try {
      const data = await consultarPlaca(plate, Number(cotacao.cve_codigo));
      form.setValue("chassis", data.chassi ?? "");
      form.setValue("model", data.MODELO ?? "");
      form.setValue("year", data.anoModelo ?? "");
      form.setValue("color", data.cor ?? "");
      form.clearErrors();
      setFieldsDisabled(true);
    } catch (error: unknown) {
      const msg =
        error instanceof AxiosError
          ? String(error.response?.data ?? "") ||
            "Não foi possível encontrar as informações do veículo."
          : "Não foi possível encontrar as informações do veículo.";
      setErrorMessage(
        `${msg} Continue o processo, nosso time irá cuidar disso...`,
      );
      setSearchFailed(true);
      setFieldsDisabled(false);
    } finally {
      setIsSearchingPlate(false);
    }
  };

  const onSubmit = () => {
    setIsSubmitting(true);
    onNext();
  };

  useEffect(() => {
    trackCotacaoStepView("veiculo");
  }, []);

  useEffect(() => {
    if (cotacao?.cve_codigo) {
      trackInfoAdicional(cotacao);
    }
  }, [cotacao]);

  return (
    <div className="">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-[50px]">
        <div className="md:col-span-2">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="bg-n-bg-2 md:p-6 p-4 rounded-[20px] border shadow-sm">
                <h3 className="text-xl font-medium text-n-primary mb-6">
                  Informações sobre o seu veículo:
                </h3>

                <div className="space-y-4">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-sm font-medium text-n-text-neutral">
                      Seu veículo é zero quilômetro?
                    </span>
                    <FormField
                      control={form.control}
                      name="isNewVehicle"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <RadioGroup
                              value={field.value}
                              onValueChange={field.onChange}
                              className="flex items-center gap-6"
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="Sim" id="new-sim" />
                                <Label
                                  htmlFor="new-sim"
                                  className="text-sm cursor-pointer"
                                >
                                  Sim
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="Não" id="new-nao" />
                                <Label
                                  htmlFor="new-nao"
                                  className="text-sm cursor-pointer"
                                >
                                  Não
                                </Label>
                              </div>
                            </RadioGroup>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>

                  {isNewVehicleValue === "Não" && (
                    <FormField
                      control={form.control}
                      name="plate"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <div className="flex gap-1">
                              <Input
                                placeholder="Insira a placa do seu veículo"
                                className="rounded-r-sm"
                                {...field}
                              />
                              <Button
                                variant="green"
                                type="button"
                                size="xs"
                                onClick={handleSearchPlate}
                                className="rounded-l-sm rounded-r-xl md:text-sm text-xs"
                                disabled={isSearchingPlate}
                              >
                                {isSearchingPlate ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Buscando...
                                  </>
                                ) : (
                                  <>
                                    Buscar
                                    <Search className="h-4 w-4 ml-1" />
                                  </>
                                )}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {searchFailed && (
                    <p className="text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm">
                      {errorMessage}
                    </p>
                  )}

                  <div className="text-sm text-n-text-neutral md:mb-2">
                    Informe os dados do seu veículo:
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 md:gap-4 gap-2">
                    <FormField
                      control={form.control}
                      name="year"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              placeholder="Ano"
                              {...field}
                              disabled={fieldsDisabled}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="model"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              placeholder="Modelo"
                              {...field}
                              disabled={true}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="chassis"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              placeholder="Chassi"
                              {...field}
                              disabled={fieldsDisabled}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="color"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              placeholder="Cor"
                              {...field}
                              disabled={fieldsDisabled}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
                <div className="border-t border-gray-200 my-6"></div>
                <h3 className="text-lg font-medium text-n-primary my-4">
                  Informações adicionais:
                </h3>

                <div className="space-y-4">
                  <div className="flex flex-col md:flex-row items-start gap-3 py-3 px-6 border border-n-atention rounded-xl">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="flex-shrink-0 mt-0.5">
                        <GoAlert className="text-n-atention h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-n-text leading-tight">
                          Confirme a data prevista para o transporte.
                        </p>
                        <p className="text-xs text-gray-600 mt-0.5">
                          Garanta a oferta e altere quando precisar, sem custo.
                        </p>
                      </div>
                    </div>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="input"
                          size="md"
                          className="h-[32px] w-full md:w-fit flex-shrink-0 md:justify-start rounded-full justify-center md:text-left text-center text-sm font-normal border-zinc-300 border bg-white hover:bg-gray-50"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? (
                            format(date, "dd/MM/yyyy", { locale: ptBR })
                          ) : (
                            <span>Selecione</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <CalendarComponent
                          mode="single"
                          selected={date}
                          onSelect={logDateChange}
                          disabled={isDateDisabled}
                          initialFocus
                          locale={ptBR}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {cotacao.cve_operante === "1" && (
                    <label
                      className={`w-full flex gap-2 items-start text-n-primary py-3 px-6 rounded-2xl cursor-pointer transition-colors border border-zinc-300 ${
                        selectedOptions.seguro ? "bg-n-bg-1" : "bg-zinc-50"
                      }`}
                    >
                      <Checkbox
                        className="mt-1"
                        checked={selectedOptions.seguro}
                        onCheckedChange={() => toggleOption("seguro")}
                      />
                      <div className="flex flex-col">
                        <span className="font-bold text-sm">
                          Adicionar Seguro Premium{" "}
                          <span className="font-normal pl-2">
                            {" "}
                            {formatMoneyBr(
                              Number(cotacao.valor_cobertura_avarias) -
                                Number(cotacao.cve_valor_seguro),
                            )}
                          </span>
                        </span>
                        <span className="text-n-text-neutral text-xs mt-1">
                          Cobertura padrão mais cobertura contra pequenas
                          avarias causadas no transporte
                        </span>
                      </div>
                      <div className="ml-auto mr-4">
                        <CustomPopover
                          iconSize={5}
                          title="Seguro Premium"
                          description="Tudo da cobertura padrão mais a cobertura contra pequenas avarias causadas no transporte"
                        />
                      </div>
                    </label>
                  )}

                  <CepOptionCard
                    tipo="coleta"
                    control={form.control}
                    preco={Number(cotacao?.cve_valor_coleta || 0)}
                    cepValue={cepColetaValue || ""}
                    selected={selectedOptions.coleta}
                    onSelectedChange={(val) => {
                      setSelectedOptions((prev) => ({ ...prev, coleta: val }));
                      updateModifierColeta(val);
                    }}
                    onCalculate={async () => {
                      await updateCEPColeta(cepColetaValue || "");
                    }}
                    showComplemento={false}
                  />

                  <CepOptionCard
                    tipo="entrega"
                    control={form.control}
                    preco={Number(cotacao?.cve_valor_entrega || 0)}
                    cepValue={cepEntregaValue || ""}
                    selected={selectedOptions.entrega}
                    onSelectedChange={(val) => {
                      setSelectedOptions((prev) => ({ ...prev, entrega: val }));
                      updateModifierEntrega(val);
                    }}
                    onCalculate={async () => {
                      await updateCEPEntrega(cepEntregaValue || "");
                    }}
                    showComplemento={false}
                  />
                </div>
              </div>

              <div className="flex justify-end mt-6 gap-4">
                <Button
                  type="button"
                  variant="border"
                  disabled={isSubmitting}
                  onClick={onBack}
                >
                  {!isSubmitting && <ArrowLeft />}
                  Voltar
                </Button>

                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Processando..." : "Ir para contrato"}
                  {!isSubmitting && <ArrowRight />}
                </Button>
              </div>

              <Disclaimer />
            </form>
          </Form>
        </div>

        {!isMobile && (
          <div className="md:col-span-1">
            <CardContactSpecialist />
            <div className="mt-5">
              <QuoteCardResume />
            </div>
          </div>
        )}
      </div>
      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 rounded-t-2xl bg-white border-t shadow-lg z-50">
          <div
            className="p-4 cursor-pointer"
            onClick={() => setIsOrderSummaryExpanded(!isOrderSummaryExpanded)}
          >
            <div className="flex items-center justify-between pb-3 border-b">
              <p className="text-lg text-n-text-green font-bold">Resumo:</p>
              {isOrderSummaryExpanded ? (
                <ChevronDown className="h-5 w-5 text-n-green" />
              ) : (
                <ChevronUp className="h-5 w-5 text-n-green" />
              )}
            </div>
            <div className="flex items-center justify-between mt-2">
              <div>
                <p className="text-sm text-n-text">Total</p>
                <p className="text-xl font-bold text-n-green">
                  {formatMoneyBr(quotePrice.total || 0)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    form.handleSubmit(onSubmit)();
                  }}
                  disabled={isSubmitting}
                >
                  Avançar
                  <ArrowRight className="ml-2 h-[16px] w-[16px]" />
                </Button>
              </div>
            </div>
          </div>

          {isOrderSummaryExpanded && (
            <div className="border-t bg-gray-50 max-h-96 overflow-y-auto">
              <QuoteCardResumeMobile />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
